import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { useCallback } from 'react'

import type { ChipInputProps } from '@src/component/chip-input'
import KeyboardList from '@src/component/keyboard-list/KeyboardList'
import { Menu } from '@src/component/menu-v2'
import useKeyboardShortcuts from '@src/lib/hooks/useKeyboardShortcuts'
import type { ThemeKey } from '@src/theme'
import Typography from '@ui/Typography'

import * as styles from './PhoneNumberSelector.css'
import type { IdentityRowInstance, Row } from './PhoneNumberSelectorController'
import type PhoneNumberSelectorController from './PhoneNumberSelectorController'
import PhoneNumberSelectorRow from './row'
import SearchBar from './search-bar'

interface PhoneNumberSelectorProps {
  controller: PhoneNumberSelectorController
  overlayThemeKey?: ThemeKey
  renderChip?: ChipInputProps<IdentityRowInstance>['renderChip']
}

const PhoneNumberSelector = ({
  controller,
  overlayThemeKey,
  renderChip,
}: PhoneNumberSelectorProps) => {
  const itemContent = useCallback(
    (row: Row) => (
      <PhoneNumberSelectorRow
        overlayThemeKey={overlayThemeKey}
        key={row.id}
        controller={controller}
        row={row}
      />
    ),
    [controller, overlayThemeKey],
  )

  useKeyboardShortcuts({
    name: 'PhoneNumberSelector',
    node: controller.inputRef,
    filter: (_, e) => !e.defaultPrevented,
    handler: (shortcut, event) => {
      if (shortcut === 'Escape' && controller.activeRow) {
        controller.closeMenu()
        event.preventDefault()
      }
    },
    dep: [controller],
  })

  return (
    <div className={styles.root}>
      <SearchBar controller={controller} renderChip={renderChip} />
      {/* eslint-disable-next-line @typescript-eslint/no-deprecated */}
      <Menu className={styles.menu}>
        {controller.hasResults ? (
          <KeyboardList
            node={controller.inputRef.current ?? document}
            allowedShortcutsAlways={['Enter', 'Escape', 'ArrowUp', 'ArrowDown']}
            controller={controller.keyboardList}
            renderItem={itemContent}
            className={cx(styles.list, { [styles.listPadding]: controller.size !== 24 })}
            pointerEvents={controller.activeRow ? 'none' : 'auto'}
          />
        ) : controller.size === 24 ? (
          <div className={styles.empty}>
            <span className={styles.emoji}>📞</span>
            <Typography variant="caption1" color="textSecondary" fontWeight="regular">
              No results found
            </Typography>
          </div>
        ) : controller.size === 30 ? (
          <div className={styles.emptySpace}></div>
        ) : null}
      </Menu>
    </div>
  )
}

export default observer(PhoneNumberSelector)
